import { createRouter, createWebHistory } from "vue-router"
import { useAuthStore } from "@/stores/auth"
import { useResourceStore } from "@/stores/resource"
import { useLoadingStore } from "@/stores/loading"
import { trackPageView } from "@/composables/useAnalytics"
import { useTheme } from "@/composables/useTheme"
import { toast } from "vue-sonner"
import { organisationRoutes } from "./modules/care"
import { talentRoutes } from "./modules/talent"
import { useFeatureFlagsStore } from "@/stores/featureFlags"
import { reloadOnceForChunkError } from "@/lib/chunkReload"
import type { Organisation, LoginCategory } from "@kiddocaremy/sdk-ts"

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            // The landing page is hidden for now; "/" goes straight to the care
            // login. To bring it back, restore the component and meta below:
            //   component: () => import("@/pages/Landing.vue"),
            //   meta: { skipVerification: true, title: "Find Certified ECE Talent & Care Careers" },
            // The query is forwarded so the guard's ?redirect= return-to target
            // survives the hop to the login screen.
            path: "/",
            name: "home",
            redirect: (to) => ({ name: "care-login", query: to.query }),
        },
        {
            // Several pages link to a bare /login. Point it at the same place as "/".
            path: "/login",
            redirect: (to) => ({ name: "care-login", query: to.query }),
        },
        {
            path: "/register",
            name: "register",
            component: () => import("@/pages/Register.vue"),
            meta: { guest: true, title: "Register" },
        },
        ...organisationRoutes,
        ...talentRoutes,
        {
            path: "/wrong-area",
            name: "wrong-area",
            component: () => import("@/pages/WrongArea.vue"),
            meta: { skipVerification: true, title: "Wrong Area" },
        },
        {
            path: "/403",
            name: "forbidden",
            component: () => import("@/pages/Forbidden.vue"),
            meta: { skipVerification: true, title: "Access Denied" },
        },
        {
            path: "/404",
            name: "not-found",
            component: () => import("@/pages/NotFound.vue"),
            meta: { skipVerification: true, title: "Not Found" },
        },
        {
            path: "/:pathMatch(.*)*",
            redirect: (to) => ({
                path: "/404",
                query: { from: to.fullPath }
            }),
        },
    ],
})

router.beforeEach(async (to, _from, next) => {
    // Start loading indicator
    const loadingStore = useLoadingStore()
    loadingStore.startLoading()

    // Apply theme based on route path
    const { applyTheme } = useTheme()
    if (to.path.startsWith('/care')) {
        applyTheme('care')
    } else if (to.path.startsWith('/talent')) {
        applyTheme('talent')
    }

    const authStore = useAuthStore()
    const resourceStore = useResourceStore()

    // Skip auth initialization for callback routes (they handle auth themselves)
    const skipAuthInit = to.meta.skipAuthInit as boolean | undefined

    if (!authStore.isInitialized && !skipAuthInit) {
        try {
            // Determine login category from route path
            let loginCategory: LoginCategory | undefined
            if (to.path.startsWith('/care')) {
                loginCategory = 'organisation'
            } else if (to.path.startsWith('/talent')) {
                loginCategory = 'talent'
            }

            const initData = await authStore.initializeAuth(loginCategory)

            if (authStore.isAuthenticated && initData?.resourceHash) {
                await resourceStore.setupCdn(initData.resourceHash)
            }
        } catch (error) {
            console.error('Failed to initialize app:', error)
        }
    }

    // For routes that skip auth init, proceed without auth or feature flag
    // checks. An OAuth callback is a cold page load, so no init has run yet and
    // the feature flags are still empty - gating here would 404 the callback and
    // break SSO sign in entirely. The callback initialises auth itself and then
    // navigates on, and that navigation runs this guard again with flags loaded.
    if (skipAuthInit) {
        next()
        return
    }

    // Check if talent hub is enabled (after init so feature flags are available).
    // Only gate once the flags have actually loaded: when initializeAuth throws,
    // or returns early on a 401, flags stay null and isTalentHubEnabled reads
    // false regardless of the real flag state. Don't let that false negative
    // bounce the user to a 404.
    const featureFlagsStore = useFeatureFlagsStore()
    if (to.path.startsWith('/talent') && featureFlagsStore.flags !== null && !featureFlagsStore.isTalentHubEnabled) {
        next({ name: "not-found" })
        return
    }

    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
    const isGuest = to.matched.some((record) => record.meta.guest)
    const requiresRole = to.meta.requiresRole as string | undefined
    const permission = to.meta.permission as string | undefined

    // Check if user is authenticated
    if (requiresAuth && !authStore.isAuthenticated) {
        toast.warning("Please login to access this page")
        let loginPath: string
        if (to.path.startsWith('/care')) {
            loginPath = 'care-login'
        } else if (to.path.startsWith('/talent')) {
            loginPath = 'talent-login'
        } else {
            loginPath = 'home'
        }
        next({
            name: loginPath,
            query: { redirect: to.fullPath },
        })
        return
    }

    // Redirect authenticated users away from guest pages to their dashboard
    if (isGuest && authStore.isAuthenticated) {
        next(authStore.getDefaultRoute())
        return
    }

    // Check verification status for authenticated users
    const skipVerification = to.meta.skipVerification as boolean | undefined

    if (authStore.isAuthenticated && !skipVerification) {
        // Determine area from route path
        let area: 'care' | 'talent' | undefined
        if (to.path.startsWith('/care')) {
            area = 'care'
        } else if (to.path.startsWith('/talent')) {
            area = 'talent'
        }

        const verificationRoute = authStore.getVerificationRoute(area)

        // If user needs verification, redirect to verification route (dashboard with dialog)
        if (verificationRoute && to.path !== verificationRoute) {
            next(verificationRoute)
            return
        }
    }

    // Check if user has the required role
    if (requiresRole && authStore.isAuthenticated) {
        if (!authStore.hasRole(requiresRole)) {
            next({
                name: "wrong-area",
                query: { attempted: requiresRole }
            })
            return
        }
    }

    // Check if organisation user has selected an org for routes that require it
    const requiresOrgSelection = to.meta.requiresOrgSelection as boolean | undefined
    if (requiresOrgSelection && authStore.isAuthenticated) {
        // Check if user is an organisation user
        const isOrgUser = authStore.hasAnyRole(['organisation_owner', 'organisation_admin', 'organisation_user'])
        if (isOrgUser) {
            const orgNo = to.params.org_no as string

            // Always validate that org_no exists in user's organizations list
            if (orgNo) {
                const orgExists = authStore.organizations.some((org: Organisation) => org.code === orgNo)

                if (!orgExists) {
                    // User doesn't have access to this org
                    toast.error("You don't have access to this organisation")
                    next({ name: "care-select-org" })
                    return
                }

                // If org exists, ensure it's selected
                if (!authStore.selectedOrganization || authStore.selectedOrganization.code !== orgNo) {
                    const org = authStore.organizations.find((org: Organisation) => org.code === orgNo)
                    if (org) {
                        authStore.selectOrganization(org)
                    }
                }
            } else {
                // No org_no in URL, redirect to select org
                toast.warning("Please select an organisation first")
                next({ name: "care-select-org" })
                return
            }
        }
    }

    // The Job Board menu items are hidden when the flag is off, so also block
    // the routes when a user opens them by URL. As with the Talent Hub check,
    // only gate once the flags have loaded.
    const requiresJobBoard = to.matched.some((record) => record.meta.requiresJobBoard)
    if (requiresJobBoard && featureFlagsStore.flags !== null) {
        // The board is a separate flag per side: care owner vs talent search.
        const isCare = to.path.startsWith('/care')
        const jobBoardEnabled = isCare
            ? featureFlagsStore.isCareJobBoardEnabled
            : featureFlagsStore.isTalentJobBoardEnabled

        if (!jobBoardEnabled) {
            if (isCare) {
                next({ name: "care-dashboard", params: { org_no: to.params.org_no } })
            } else {
                next({ name: "talent-dashboard" })
            }
            return
        }
    }

    // Credit packages (Care Hub owner) — hidden in the menu when off, so also
    // block the Plan & Pricing route when opened by URL, once flags have loaded.
    const requiresCredits = to.matched.some((record) => record.meta.requiresCredits)
    if (requiresCredits && featureFlagsStore.flags !== null && !featureFlagsStore.isCareCreditsEnabled) {
        next({ name: "care-dashboard", params: { org_no: to.params.org_no } })
        return
    }

    // Check if user has the required permission
    if (permission && authStore.isAuthenticated) {
        if (!authStore.hasPermission(permission)) {
            toast.error("You don't have permission to access this page")
            next("/403")
            return
        }
    }

    next()
})

const APP_NAME = import.meta.env.VITE_APP_NAME || 'KiddoCare Connect'

router.afterEach((to: any) => {
    // Stop loading indicator
    const loadingStore = useLoadingStore()
    loadingStore.stopLoading()

    // Set page title from route meta
    const pageTitle = to.meta.title as string | undefined
    document.title = pageTitle ? `${pageTitle} | ${APP_NAME}` : APP_NAME

    trackPageView(pageTitle || APP_NAME, to.fullPath)
})

// A stale tab navigating to a lazy route whose chunk no longer exists — because
// Cloudflare now serves a newer build — reloads once to recover instead of erroring.
router.onError((error) => {
    reloadOnceForChunkError(error)
})

export default router
